import React, { useEffect, useRef, useState } from 'react'

import { Box, BoxProps, DisplayPosition, withCitrusTheme } from '@gousto-internal/citrus-react'

import { CWCPlayPauseButton } from '../CWCPlayPauseButton/CWCPlayPauseButton'
import { useAutoplayOnIntersect } from '../useAutoplayOnIntersect'
import { LandingPageContentSections, trackVideoPlayback, TrackingActions } from '../../tracking'

const DEFAULT_GRADIENT =
  'linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0) 26%, rgba(0,0,0,0) 52%, rgba(0,0,0,0.78) 100%)'

const GradientOverlay = ({ gradient, ...props }: BoxProps & { gradient: string }) => {
  const Component = withCitrusTheme(Box, () => ({ background: gradient }))
  return <Component {...props} />
}

/**
 * Optional tracking descriptor. When present, an explicit control click fires `click_play_video`
 * when starting playback and `click_pause_video` when pausing — always from the click handler only,
 * never from the video element's own play/pause listeners (which also fire on scroll-driven autoplay).
 */
export type VideoPlaybackTracking = {
  section: LandingPageContentSections
  tile: string
}

export type CWCVideoCardProps = {
  videoURL: string
  /** Figure width — accepts a Citrus responsive value. */
  width?: BoxProps['width']
  /** Figure height — accepts a Citrus responsive value. */
  height?: BoxProps['height']
  /** Figure background, visible before the video paints / behind the object-cover video. */
  background?: string
  /** Corner radius of the figure. */
  borderRadius?: string
  /** Drop shadow on the figure. Defaults to the Reviews card shadow. */
  boxShadow?: string
  /** Gradient overlay for control/label legibility. Pass `null` to omit. */
  gradient?: string | null
  /** Optional play/pause tracking descriptor, fired only on explicit control clicks. */
  tracking?: VideoPlaybackTracking
  /** Card title (step/recipe name) appended to the play/pause control's accessible name. */
  mediaLabel?: string
  /** Overlay content rendered above the video/gradient (e.g. the Reviews name + rating). */
  children?: React.ReactNode
  'data-testid'?: string
}

/**
 * Shared video-card mechanics used by both the Reviews Section and How It Works: a muted, looping,
 * inline `<video preload="none">` with the play/pause control revealed on `onCanPlay`, a gradient
 * overlay for legibility, and autoplay-on-intersect that respects an explicit user pause. Section-
 * specific chrome (Reviews' name + rating, or How It Works' below-figure labels) is composed by the
 * caller — Reviews via `children` (an overlay), How It Works outside this figure.
 *
 * This card owns the playback logic: the displayed play/pause state (kept in sync with the video's
 * own events so scroll-driven autoplay is reflected too), and the toggle handler that plays/pauses,
 * records the explicit user pause, and fires playback tracking. `CWCPlayPauseButton` is a dumb
 * control we inject that state and handler into.
 */
export function CWCVideoCard({
  videoURL,
  width = '240px',
  height = '380px',
  background,
  borderRadius = '16px',
  boxShadow = '0px 24px 50px -26px rgba(0,0,0,0.7)',
  gradient = DEFAULT_GRADIENT,
  tracking,
  mediaLabel,
  children,
  'data-testid': testId = 'recipe-video-card',
}: CWCVideoCardProps) {
  const [showControls, setShowControls] = useState(false)
  const [isPlaying, setIsPlaying] = useState(true)
  const videoRef = useRef<HTMLVideoElement>(null)
  // Tracks whether the user explicitly paused this video via the control, so autoplay-on-intersect
  // won't resume it when the card scrolls back into view.
  const userPausedRef = useRef(false)

  useAutoplayOnIntersect(videoRef, userPausedRef)

  // Source the displayed state from the video's own play/pause events, not just the toggle handler —
  // useAutoplayOnIntersect can play/pause it independently, and the control must stay in sync.
  useEffect(() => {
    const video = videoRef.current
    if (!video) {
      return undefined
    }

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [])

  // Fires ONLY on explicit control activation — never from the video's own play/pause events — so
  // scroll-driven autoplay is not tracked.
  const handleToggle = () => {
    const video = videoRef.current
    if (!video) {
      return
    }

    if (video.paused) {
      userPausedRef.current = false
      video.play()
      if (tracking) {
        trackVideoPlayback(TrackingActions.CLICK_PLAY_VIDEO, tracking.section, tracking.tile)
      }
    } else {
      userPausedRef.current = true
      video.pause()
      if (tracking) {
        trackVideoPlayback(TrackingActions.CLICK_PAUSE_VIDEO, tracking.section, tracking.tile)
      }
    }
  }

  return (
    <Box
      data-testid={testId}
      position={DisplayPosition.Relative}
      width={width}
      height={height}
      style={{
        borderRadius,
        overflow: 'hidden',
        boxShadow,
        ...(background ? { background } : {}),
      }}
    >
      <video
        muted
        loop
        playsInline
        preload="none"
        src={videoURL}
        style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        aria-hidden="true"
        ref={videoRef}
        onCanPlay={() => setShowControls(true)}
      />
      {gradient && (
        <GradientOverlay
          data-testid="video-card-gradient"
          gradient={gradient}
          position={DisplayPosition.Absolute}
          top="0"
          left="0"
          width="100%"
          height="100%"
        />
      )}
      {showControls && (
        <Box position={DisplayPosition.Absolute} top="16px" right="16px">
          <CWCPlayPauseButton
            isPlaying={isPlaying}
            onToggle={handleToggle}
            mediaLabel={mediaLabel}
          />
        </Box>
      )}
      {children}
    </Box>
  )
}
